import { all, takeLatest, call, put } from 'redux-saga/effects';
import {
  logoutUserActions,
  getUserInfoActions,
  signInWithOAuthActions,
} from '@/redux/actions';
import apiPortalService from '@/services/api-portal';
import membershipService from '@/services/membership';
import authService, { authHelpers } from '@/services/auth';
import { ActionType } from 'deox';
import { navigate } from '@reach/router';
import 'antd/lib/modal/style';
import { AxiosError } from 'axios';
import env from '@/env';
import { getAuthUser, getSubdomain } from '@/utils';
import { AuthUser } from '@/types';
import serviceDirectoryService from '@/services/service-directory';

function* handleOrgRedirection(memberships: any[], hasAdminPermission: boolean) {
  const organisationsOwner = memberships.filter(
    (org: { roleName: string }) =>
      org.roleName?.toLowerCase() === env.roles.organisationOwner.toLowerCase(),
  );

  const organisationShouldAccess = organisationsOwner.length
    ? organisationsOwner[0]
    : memberships[0];

  authHelpers.storeOrgToken(organisationShouldAccess.token);

  try {
    const subdomain = getSubdomain();
    if (subdomain) {
      const domainData: { subdomain: string } = yield call(serviceDirectoryService.fetchEntity, subdomain);
      window.location.href = `https://${domainData.subdomain ?? subdomain}.${env.domain}`;
    } else {
      const userOrganisation: { [key: string]: any } = yield call(
        apiPortalService.fetchOrganisation,
        organisationShouldAccess.organisationId,
      );

      memberships.length > 1
        ? navigate('/auth/organizations')
        : (window.location.href = `https://${userOrganisation.subdomain}.${env.domain}`);
    }
  } catch (error) {
    const errorCode = (error as AxiosError)?.response?.status;
    if (errorCode === 404) {
      hasAdminPermission
        ? (window.location.href = `https://${env.domain}/dashboard`)
        : navigate('/auth/bank-detail');
    } else {
      yield put(signInWithOAuthActions.failure(new Error('Failed fetching organisation')));
      window.location.href = env.rootUrl;
    }

  }
}

function* fetchAndHandleUserInfo(hasAdminPermission: boolean) {
  try {
    const { data } = yield call(membershipService.getUserInfo);
    const { memberships, email, userId } = data;

    if (!memberships.length) {
      window.location.href = env.rootUrl;
      return;
    }

    yield put(getUserInfoActions.success(email, memberships, userId));
    window.location.href = env.rootUrl;
  } catch (err) {
    yield put(getUserInfoActions.failure(err));
    window.location.href = env.rootUrl;
  }
}

export function* signInWithOAuthSaga(action: ActionType<typeof signInWithOAuthActions.request>) {
  try {
    const response: unknown = yield call(authService.signInWithOAuth, action.payload.authData);

    const { id_token, access_token, refresh_token }: any = response;

    window.cachedAuthBearer = null;
    window.cachedOrgToken = null;

    authHelpers.storeIdToken(id_token);
    authHelpers.storeRefreshToken(refresh_token);
    authHelpers.storeAccessToken(access_token);

    const { roles } = getAuthUser() as AuthUser;
    const hasAdminPermission =
      roles.includes(env.roles.superAdmin) || roles.includes(env.roles.directoryAdmin);

    yield call(fetchAndHandleUserInfo, hasAdminPermission);
    yield put(signInWithOAuthActions.success());
  } catch (err) {
    yield put(
      signInWithOAuthActions.failure(
        new Error('Login failed due to invalid password or non-activation'),
      ),
    );
  }
}



export function* logoutUserSaga() {
  try {
    authHelpers.clearTokens();
    yield call(authService.logOutOAuth);
    yield put(logoutUserActions.success());
  } catch (err) {
    yield put(logoutUserActions.failure(err));
  }
}

export default function* root() {
  yield all([
    takeLatest(logoutUserActions.request, logoutUserSaga),
    takeLatest(signInWithOAuthActions.request, signInWithOAuthSaga),
  ]);
}
